/**
 * "What's new" widget: a bell with an unread badge, opening a panel of recent
 * product updates for the billing product. Ported from Prism's widget.
 *
 * Fetches the same-origin `GET /api/product-updates` (the BFF proxies
 * leon-intake `/v1/updates?product=billing` and degrades to `[]`). When there
 * is nothing to show the trigger is not rendered at all: never a bell that
 * opens onto nothing. Seen state lives in localStorage under
 * `spoonity-billing-updates-seen` (the id of the newest update the person opened).
 *
 * `docked` is the top bar's 32px icon button; `floating` is a corner pill for
 * surfaces without a top bar.
 */

import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, IconButton, Modal, Tooltip, TooltipContent, TooltipTrigger } from '@/lib/macondo';
import { endpoints } from '@/feedback/config';
import { withBase } from '@/lib/basePath';
import { MESSAGES } from '@/feedback/strings';

/** One row of leon-intake's `GET /v1/updates`, as the BFF passes it through. */
export interface ProductUpdate {
  id: number;
  title: string;
  body: string;
  products?: string[];
  publishedAt: string;
}

export const LAST_SEEN_KEY = 'spoonity-billing-updates-seen';

const BELL_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

function getLastSeen(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function setLastSeen(id: number): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, String(id));
  } catch {
    /* private mode: the unread count just will not persist */
  }
}

async function fetchUpdates(): Promise<ProductUpdate[]> {
  const res = await fetch(withBase(endpoints.updates), { credentials: 'same-origin' });
  if (!res.ok) return [];
  const data = (await res.json().catch(() => null)) as { updates?: unknown } | null;
  return Array.isArray(data?.updates) ? (data.updates as ProductUpdate[]) : [];
}

export type ProductUpdatesWidgetVariant = 'floating' | 'docked';

export function ProductUpdatesWidget({ variant = 'docked' }: { variant?: ProductUpdatesWidgetVariant } = {}) {
  const [open, setOpen] = useState(false);
  const [updates, setUpdates] = useState<ProductUpdate[]>([]);
  const [lastSeen, setLastSeenState] = useState<string | null>(() => getLastSeen());

  useEffect(() => {
    let cancelled = false;
    fetchUpdates()
      .then((list) => {
        if (cancelled) return;
        setUpdates([...list].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()));
      })
      .catch(() => {
        if (!cancelled) setUpdates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dateFormat = useMemo(() => new Intl.DateTimeFormat(undefined), []);

  const unreadCount = useMemo(() => {
    if (!lastSeen) return updates.length;
    const idx = updates.findIndex((u) => String(u.id) === lastSeen);
    return idx === -1 ? updates.length : idx;
  }, [updates, lastSeen]);

  if (updates.length === 0) return null;

  function handleOpen() {
    setOpen(true);
    const newest = updates[0];
    if (newest) {
      setLastSeen(newest.id);
      setLastSeenState(String(newest.id));
    }
  }

  const triggerLabel = unreadCount > 0 ? MESSAGES.updatesTriggerUnread(unreadCount) : MESSAGES.updatesTrigger;
  const badgeText = unreadCount > 9 ? '9+' : unreadCount;

  return (
    <>
      {variant === 'docked' ? (
        <span className="relative inline-flex">
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                type="button"
                size="sm"
                variant="ghost-square"
                aria-label={triggerLabel}
                onClick={handleOpen}
                className="h-800 w-800 rounded-200"
                icon={BELL_ICON}
              />
            </TooltipTrigger>
            <TooltipContent>{triggerLabel}</TooltipContent>
          </Tooltip>
          {unreadCount > 0 && (
            <Badge
              variant="critical"
              size="sm"
              aria-hidden="true"
              className="pointer-events-none absolute -right-[2px] -top-[2px] flex h-[15px] min-w-[15px] items-center justify-center px-[3px] text-[9.5px] leading-none"
            >
              {badgeText}
            </Badge>
          )}
        </span>
      ) : (
        <Button
          type="button"
          variant="tertiary"
          onClick={handleOpen}
          aria-label={triggerLabel}
          className="fixed bottom-[4.75rem] right-[1.5rem] z-[40] rounded-full shadow-md"
        >
          {BELL_ICON}
          {MESSAGES.updatesTrigger}
          {unreadCount > 0 && (
            <Badge variant="critical" size="sm" aria-hidden="true" className="pointer-events-none">
              {badgeText}
            </Badge>
          )}
        </Button>
      )}

      <Modal open={open} onOpenChange={setOpen} title={MESSAGES.updatesTitle}>
        <div className="max-h-96 space-y-4 overflow-y-auto">
          {updates.map((u) => (
            <article key={u.id} className="border-border-primary border-b pb-3 last:border-0">
              <h3 className="text-callout-semibold text-typography-primary">{u.title}</h3>
              <p className="text-callout text-typography-secondary mt-1">{u.body}</p>
              <p className="text-caption text-typography-tertiary mt-1">{dateFormat.format(new Date(u.publishedAt))}</p>
            </article>
          ))}
        </div>
      </Modal>
    </>
  );
}
